import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import themeManager from '../themeManager.js';
import { useContent } from '../hooks/useContent';
import { enrichSocialLinks } from '../api/socialIcons';
import { MobileMenuLinks } from './MenuNav';
import HamburgerButton from './HamburgerButton';
import '../homepage.css';

const EXTRA_MENU = [
  { href: '/announcements', label: 'Announcements', icon: 'megaphone' },
  { href: '/media', label: 'Media', icon: 'media' },
  { href: '/roadmap', label: 'Full Roadmap', icon: 'list' },
];

export default function SiteLayout({ children, pageClass = 'site-page' }) {
  const navigate = useNavigate();
  const [currentTheme, setCurrentTheme] = useState(themeManager.getCurrentTheme());
  const { content } = useContent();
  const socialLinks = enrichSocialLinks(content.socials);

  useEffect(() => {
    const checkTheme = () => setCurrentTheme(themeManager.getCurrentTheme());
    const interval = setInterval(checkTheme, 30000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    document.body.classList.remove('daytime', 'nighttime');
    document.body.classList.add(currentTheme);

    const hamburger = document.querySelector('.hamburger');
    const panel = document.querySelector('.menu-panel');
    const scrim = document.querySelector('.menu-scrim');
    if (!hamburger || !panel || !scrim) return;

    const menuLinks = panel.querySelectorAll('.menu-link');
    const anchorLinks = document.querySelectorAll(
      '.site-nav a[href^="#"], .menu-link[href^="#"], .menu-link[href^="/#"], .site-nav a[href^="/#"]'
    );

    const openMenu = () => {
      document.body.classList.add('menu-open');
      hamburger.setAttribute('aria-expanded', 'true');
      panel.setAttribute('aria-hidden', 'false');
      scrim.hidden = false;
    };

    const closeMenu = () => {
      document.body.classList.remove('menu-open');
      hamburger.setAttribute('aria-expanded', 'false');
      panel.setAttribute('aria-hidden', 'true');
      scrim.hidden = true;
    };

    const toggleMenu = () => {
      document.body.classList.contains('menu-open') ? closeMenu() : openMenu();
    };

    const goHomeHash = (hash) => {
      navigate('/');
      setTimeout(() => {
        const target = document.getElementById(hash.replace('#', ''));
        if (target) target.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    };

    const onMenuLinkClick = (e) => {
      const href = e.currentTarget.getAttribute('href');
      if (href === '/') {
        e.preventDefault();
        navigate('/');
        closeMenu();
      } else if (href?.startsWith('/#')) {
        e.preventDefault();
        goHomeHash(href.slice(2));
        closeMenu();
      } else if (href?.startsWith('/') && !href.startsWith('/#')) {
        closeMenu();
      }
    };

    const onAnchorClick = (e) => {
      const href = e.currentTarget.getAttribute('href');
      if (href?.startsWith('#')) {
        e.preventDefault();
        const target = document.querySelector(href);
        if (target) target.scrollIntoView({ behavior: 'smooth' });
        closeMenu();
      } else if (href?.startsWith('/#')) {
        e.preventDefault();
        goHomeHash(href.slice(2));
        closeMenu();
      }
    };

    const onHamburgerClick = () => toggleMenu();
    const onHamburgerKey = (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        toggleMenu();
      }
    };
    const onEscape = (e) => {
      if (e.key === 'Escape' && document.body.classList.contains('menu-open')) {
        closeMenu();
        hamburger.focus();
      }
    };

    hamburger.addEventListener('click', onHamburgerClick);
    scrim.addEventListener('click', closeMenu);
    hamburger.addEventListener('keydown', onHamburgerKey);
    document.addEventListener('keydown', onEscape);
    menuLinks.forEach((link) => link.addEventListener('click', onMenuLinkClick));
    anchorLinks.forEach((link) => link.addEventListener('click', onAnchorClick));

    return () => {
      hamburger.removeEventListener('click', onHamburgerClick);
      scrim.removeEventListener('click', closeMenu);
      hamburger.removeEventListener('keydown', onHamburgerKey);
      document.removeEventListener('keydown', onEscape);
      menuLinks.forEach((link) => link.removeEventListener('click', onMenuLinkClick));
      anchorLinks.forEach((link) => link.removeEventListener('click', onAnchorClick));
    };
  }, [navigate, currentTheme]);

  return (
    <div className={`App ${pageClass}`}>
      <header className="site-header">
        <a href="/" className="site-logo" aria-label="Gumball Buddies home">
          <img src="/imgs/gum_logo.png" alt="Gumball Buddies" className="site-logo-img" />
        </a>

        <nav className="site-nav" aria-label="Main navigation">
          <a href="/#about">About</a>
          <a href="/roadmap">Roadmap</a>
          <a href="/announcements">Announcements</a>
          <a href="/media">Media</a>
          <a href="/#socials">Community</a>
        </nav>

        <a href="/#socials" className="site-header-cta">Join Us</a>

        <HamburgerButton />
      </header>

      <nav className="menu-panel" aria-hidden="true">
        <div className="menu-panel-inner">
          <p className="menu-brand">Gumball Buddies</p>
          <ul className="menu-list">
            <MobileMenuLinks extraItems={EXTRA_MENU} />
          </ul>
        </div>
      </nav>

      <div className="menu-scrim" hidden />

      <main className="site-main">{children}</main>

      <footer className="site-footer">
        <div className="footer-inner">
          <a href="/" className="footer-logo" aria-label="Gumball Buddies home">
            <img src="/imgs/gum_logo.png" alt="" />
          </a>
          <nav className="footer-nav" aria-label="Footer navigation">
            <a href="/#about">About</a>
            <a href="/roadmap">Roadmap</a>
            <a href="/announcements">Announcements</a>
            <a href="/media">Media</a>
            <a href="/#socials">Community</a>
          </nav>
          <div className="footer-socials">
            {socialLinks.map((s) => (
              <a
                key={s.label}
                className={`footer-social ${s.cls}`}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
              >
                <span className="icon" aria-hidden="true">{s.icon}</span>
              </a>
            ))}
          </div>
          <p className="footer-copy">&copy; {new Date().getFullYear()} Gumball Buddies. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
